import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { LogOut, Calendar, Camera, Check, AlertTriangle, Sparkles, Clock, Upload, X, ShieldCheck, ChevronRight, Send, PlusCircle } from 'lucide-react';
import { FadeIn, FadeInStagger, FadeInStaggerItem } from '../components/ui/AnimatedSection';
import AdminProtocols from '../components/admin/AdminProtocols';

type Intervention = {
  id: string;
  prestation_id: string;
  scheduled_at: string;
  duration_hours: number;
  status: string;
  collaborator_id: string | null;
  prestation: {
    custom_name: string;
    type: string;
    client: {
      full_name: string;
      billing_details: any;
    }
  }
};

export default function CollaboratorDashboard() {
  const [loading, setLoading] = useState(true);
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [availableInterventions, setAvailableInterventions] = useState<Intervention[]>([]);
  const [user, setUser] = useState<any>(null);
  const [clients, setClients] = useState<any[]>([]);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'planning' | 'available' | 'protocols' | 'messages' | 'settings'>('planning');

  // Messages States
  const [requests, setRequests] = useState<any[]>([]);
  const [activeRequest, setActiveRequest] = useState<any | null>(null);
  const [messageContent, setMessageContent] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // Report Modal States
  const [activeIntervention, setActiveIntervention] = useState<Intervention | null>(null);
  const [generalNotes, setGeneralNotes] = useState('');
  const [problems, setProblems] = useState('');
  const [specialTasks, setSpecialTasks] = useState('');
  const [nextFocus, setNextFocus] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [reportLoading, setReportLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Settings States
  const [settingsFullName, setSettingsFullName] = useState('');
  const [settingsPassword, setSettingsPassword] = useState('');
  const [settingsAddress, setSettingsAddress] = useState('');
  const [settingsPostalCode, setSettingsPostalCode] = useState('');
  const [settingsCity, setSettingsCity] = useState('');
  const [settingsVatNumber, setSettingsVatNumber] = useState('');
  const [settingsSiret, setSettingsSiret] = useState('');
  const [settingsIban, setSettingsIban] = useState('');
  const [settingsMessage, setSettingsMessage] = useState('');
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Edit Intervention States
  const [editingInterventionId, setEditingInterventionId] = useState<string | null>(null);
  const [editIDate, setEditIDate] = useState('');
  const [editITime, setEditITime] = useState('');
  const [editIDuration, setEditIDuration] = useState(2);

  // Unassign Modal States
  const [unassignModalIntervention, setUnassignModalIntervention] = useState<string | null>(null);
  const [unassignReason, setUnassignReason] = useState('');
  const [unassignLoading, setUnassignLoading] = useState(false);

  useEffect(() => {
    const checkUser = async () => {
      if (!supabase) {
        navigate('/login');
        return;
      }
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
        return;
      }
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
        
      if (!profile || profile.role !== 'collaborateur') {
        navigate('/dashboard'); 
        return;
      }
      setSettingsFullName(profile.full_name);
      if (profile.billing_details) {
        setSettingsAddress(profile.billing_details.address || '');
        setSettingsPostalCode(profile.billing_details.postalCode || '');
        setSettingsCity(profile.billing_details.city || '');
        setSettingsVatNumber(profile.billing_details.vatNumber || '');
        setSettingsSiret(profile.billing_details.siret || '');
        if (profile.billing_details.iban) {
          setSettingsIban(`•••• •••• •••• ${profile.billing_details.iban.slice(-4)}`);
        }
      }

      setUser(session.user);
      fetchInterventions(session.user.id);
    };
    checkUser();
  }, [navigate]);

  useEffect(() => {
    let interval: any;
    if (user) {
      interval = setInterval(() => {
        fetchInterventions(user.id);
      }, 30000);
    }
    return () => clearInterval(interval);
  }, [user, activeRequest]);

  const fetchInterventions = async (collaboratorId: string) => {
    if (!supabase) return;
    
    // Fetch user's interventions
    const { data: myData } = await supabase
      .from('interventions')
      .select(`
        *,
        prestation:prestations(
          custom_name, type,
          client:profiles(full_name, billing_details)
        )
      `)
      .eq('collaborator_id', collaboratorId)
      .order('scheduled_at', { ascending: true });
      
    if (myData) setInterventions(myData as any);

    // Fetch available (unassigned) interventions
    const { data: availableData } = await supabase
      .from('interventions')
      .select(`
        *,
        prestation:prestations(
          custom_name, type,
          client:profiles(full_name, billing_details)
        )
      `)
      .is('collaborator_id', null)
      .eq('status', 'a_venir')
      .order('scheduled_at', { ascending: true });

    if (availableData) setAvailableInterventions(availableData as any);
    
    // Fetch related requests (where this collaborator has interventions for the client)
    const { data: reqs } = await supabase
      .from('client_requests')
      .select('*, client:profiles(full_name), messages(*, sender:profiles(full_name, role))')
      .order('created_at', { ascending: false });
      
    if (reqs) {
      reqs.forEach(r => {
        if (r.messages) r.messages.sort((a:any, b:any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      });
      setRequests(reqs);

      if (activeRequest) {
        const updatedReq = reqs.find(r => r.id === activeRequest.id);
        if (updatedReq) setActiveRequest(updatedReq);
      }
    }
    
    // Fetch clients for protocols
    const { data: clientsData } = await supabase.from('profiles').select('*').eq('role', 'client').order('full_name');
    if (clientsData) {
      setClients(clientsData);
    }

    setLoading(false);
  };

  const handleAssign = async (interventionId: string) => {
    if (!supabase || !user) return;
    try {
      const { error } = await supabase
        .from('interventions')
        .update({ collaborator_id: user.id })
        .eq('id', interventionId);
      if (error) throw error;
      fetchInterventions(user.id);
    } catch (err: any) {
      alert("Erreur lors de l'affectation : " + err.message);
    }
  };

  const handleUnassignClick = (interventionId: string) => {
    setUnassignModalIntervention(interventionId);
    setUnassignReason('');
  };

  const submitUnassign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !user || !unassignModalIntervention || !unassignReason.trim()) return;
    setUnassignLoading(true);
    try {
      // Enregistrer la justification
      const { error: logError } = await supabase.from('unassignment_logs').insert({
        intervention_id: unassignModalIntervention,
        collaborator_id: user.id,
        reason: unassignReason
      });
      if (logError) throw logError;

      // Désassigner l'intervention
      const { error } = await supabase
        .from('interventions')
        .update({ collaborator_id: null })
        .eq('id', unassignModalIntervention);
      if (error) throw error;
      
      setUnassignModalIntervention(null);
      fetchInterventions(user.id);
    } catch (err: any) {
      alert("Erreur lors du désistement : " + err.message);
    } finally {
      setUnassignLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageContent.trim() || !activeRequest || !supabase || !user) return;
    setSendingMessage(true);
    try {
      const { error } = await supabase.from('messages').insert({
        request_id: activeRequest.id,
        sender_id: user.id,
        content: messageContent
      });
      if (error) throw error;
      setMessageContent('');
      fetchInterventions(user.id);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSendingMessage(false);
    }
  };

  const markAsRead = async (req: any) => {
    if (!supabase || !user) return;
    const unreadMessages = (req.messages || []).filter((m: any) => !m.read_by?.includes(user.id));
    if (unreadMessages.length === 0) return;

    for (const msg of unreadMessages) {
      const updatedReadBy = [...(msg.read_by || []), user.id];
      await supabase.from('messages').update({ read_by: updatedReadBy }).eq('id', msg.id);
    }
    fetchInterventions(user.id);
  };

  const unreadCount = requests.reduce((acc, req) => {
    return acc + (req.messages?.filter((m: any) => !m.read_by?.includes(user?.id) && m.sender_id !== user?.id).length || 0);
  }, 0);

  const handleLogout = async () => {
    if (supabase) {
      await supabase.auth.signOut();
    }
    navigate('/login');
  };

  const handleUpdateSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSettingsLoading(true);
    setSettingsMessage('');
    try {
      if (!supabase || !user) return;
      
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('billing_details')
        .eq('id', user.id)
        .single();

      const existingBilling = currentProfile?.billing_details || {};
      const updatedIban = settingsIban.includes('••••') ? existingBilling.iban : settingsIban;

      const newBillingDetails = {
        ...existingBilling,
        address: settingsAddress,
        postalCode: settingsPostalCode,
        city: settingsCity,
        vatNumber: settingsVatNumber,
        siret: settingsSiret,
        iban: updatedIban
      };

      const updatePayload: any = {
        billing_details: newBillingDetails
      };

      if (settingsFullName) {
        updatePayload.full_name = settingsFullName;
      }

      await supabase.from('profiles').update(updatePayload).eq('id', user.id);
      if (settingsPassword) {
        const { error } = await supabase.auth.updateUser({ password: settingsPassword });
        if (error) throw error;
      }
      setSettingsMessage('Profil mis à jour avec succès.');
      setSettingsPassword('');
    } catch (err: any) {
      setSettingsMessage(err.message);
    } finally {
      setSettingsLoading(false);
    }
  };

  const updateStatus = async (id: string, newStatus: string) => {
    if (!supabase) return;
    await supabase.from('interventions').update({ status: newStatus }).eq('id', id);
    if (user) fetchInterventions(user.id);
  };

  const startEditIntervention = (i: any) => {
    setEditingInterventionId(i.id);
    const date = new Date(i.scheduled_at);
    setEditIDate(date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0'));
    setEditITime(String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0'));
    setEditIDuration(i.duration_hours || 2);
  };

  const handleSaveInterventionEdit = async (id: string) => {
    try {
      if (!supabase) return;
      const scheduledDateTime = new Date(`${editIDate}T${editITime}:00`).toISOString();
      const { error } = await supabase.from('interventions').update({
        scheduled_at: scheduledDateTime,
        duration_hours: editIDuration
      }).eq('id', id);
      if (error) throw error;
      setEditingInterventionId(null);
      if (user) fetchInterventions(user.id);
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setPhotos([...photos, ...Array.from(e.target.files)]);
    }
  };

  const removePhoto = (index: number) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const submitReport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !activeIntervention || !user) return;
    setReportLoading(true);

    try {
      const uploadedUrls: string[] = [];
      for (const file of photos) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${activeIntervention.id}/${Math.random()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('reports_media')
          .upload(fileName, file);
          
        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('reports_media')
          .getPublicUrl(fileName);
          
        uploadedUrls.push(publicUrl);
      }

      const { error: reportError } = await supabase.from('reports').insert({
        intervention_id: activeIntervention.id,
        general_notes: generalNotes,
        problems_encountered: problems,
        special_tasks: specialTasks,
        next_time_focus: nextFocus,
        photos: uploadedUrls
      });

      if (reportError) throw reportError;

      await supabase.from('interventions').update({ status: 'termine' }).eq('id', activeIntervention.id);

      setActiveIntervention(null);
      setGeneralNotes(''); setProblems(''); setSpecialTasks(''); setNextFocus(''); setPhotos([]);
      fetchInterventions(user.id);

    } catch (err) {
      console.error(err);
      alert('Erreur lors de la soumission du rapport.');
    } finally {
      setReportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-gold-200 border-t-gold-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  const upcomingInterventions = interventions.filter(i => i.status === 'a_venir' || i.status === 'en_cours');
  const pastInterventions = interventions.filter(i => i.status === 'termine');

  return (
    <div className="min-h-screen flex bg-cream-100 font-sans text-black-deep">
      
      {/* Sidebar */}
      <aside className="w-72 bg-white shadow-2xl flex flex-col fixed h-full z-20 hidden md:flex">
        <div className="p-8 border-b border-gray-100 flex flex-col items-center">
          <img src="/assets/logo_v2.webp" alt="L'Art du Ménage" className="h-16 w-auto mb-4" />
          <h2 className="font-serif tracking-widest text-lg">COLLABORATEUR</h2>
        </div>
        
        <nav className="flex-grow py-8 px-4 space-y-2">
          <button onClick={() => setActiveTab('planning')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'planning' ? 'bg-black-deep text-white shadow-md' : 'text-gray-500 hover:bg-cream-50 hover:text-black-deep'}`}>
            <span className="font-medium text-sm tracking-wider uppercase">Mon Planning</span>
            {activeTab === 'planning' && <ChevronRight size={16} className="text-gold-500" />}
          </button>
          
          <button onClick={() => setActiveTab('available')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'available' ? 'bg-black-deep text-white shadow-md' : 'text-gray-500 hover:bg-cream-50 hover:text-black-deep'}`}>
            <span className="font-medium text-sm tracking-wider uppercase">Disponibles</span>
            {activeTab === 'available' && <ChevronRight size={16} className="text-gold-500" />}
          </button>
          
          <button onClick={() => setActiveTab('protocols')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'protocols' ? 'bg-black-deep text-white shadow-md' : 'text-gray-500 hover:bg-cream-50 hover:text-black-deep'}`}>
            <span className="font-medium text-sm tracking-wider uppercase">Protocoles</span>
            {activeTab === 'protocols' && <ChevronRight size={16} className="text-gold-500" />}
          </button>

          <button onClick={() => setActiveTab('messages')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'messages' ? 'bg-black-deep text-white shadow-md' : 'text-gray-500 hover:bg-cream-50 hover:text-black-deep'}`}>
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm tracking-wider uppercase">Messagerie</span>
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{unreadCount}</span>
              )}
            </div>
            {activeTab === 'messages' && <ChevronRight size={16} className="text-gold-500" />}
          </button>

          <button onClick={() => setActiveTab('settings')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'settings' ? 'bg-black-deep text-white shadow-md' : 'text-gray-500 hover:bg-cream-50 hover:text-black-deep'}`}>
            <span className="font-medium text-sm tracking-wider uppercase">Paramètres</span>
            {activeTab === 'settings' && <ChevronRight size={16} className="text-gold-500" />}
          </button>
        </nav>

        <div className="p-4 border-t border-gray-100">
          <button 
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 text-gray-500 hover:text-red-500 transition-colors uppercase tracking-widest text-sm font-bold"
          >
            <LogOut size={18} /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:ml-72 flex flex-col h-screen overflow-hidden bg-cream-50">
        
        {/* Top Header Mobile */}
        <header className="md:hidden bg-white px-6 py-4 shadow-sm flex justify-between items-center z-10 sticky top-0">
          <img src="/assets/logo_v2.webp" alt="L'Art du Ménage" className="h-8 w-auto" />
          <button onClick={handleLogout} className="text-gray-500">
            <LogOut size={24} />
          </button>
        </header>

        {/* Mobile Tabs */}
        <div className="md:hidden bg-white border-b border-gray-100 flex overflow-x-auto sticky top-[64px] z-10">
          <button onClick={() => setActiveTab('planning')} className={`flex-1 py-4 text-xs font-bold uppercase tracking-widest text-center whitespace-nowrap px-4 border-b-2 transition-colors ${activeTab === 'planning' ? 'border-black-deep text-black-deep' : 'border-transparent text-gray-400'}`}>
            Planning
          </button>
          <button onClick={() => setActiveTab('available')} className={`flex-1 py-4 text-xs font-bold uppercase tracking-widest text-center whitespace-nowrap px-4 border-b-2 transition-colors ${activeTab === 'available' ? 'border-black-deep text-black-deep' : 'border-transparent text-gray-400'}`}>
            Disponibles
          </button>
          <button onClick={() => setActiveTab('protocols')} className={`flex-1 py-4 text-xs font-bold uppercase tracking-widest text-center whitespace-nowrap px-4 border-b-2 transition-colors ${activeTab === 'protocols' ? 'border-black-deep text-black-deep' : 'border-transparent text-gray-400'}`}>
            Protocoles
          </button>
          <button onClick={() => setActiveTab('messages')} className={`flex-1 py-4 flex justify-center items-center gap-1 text-xs font-bold uppercase tracking-widest text-center whitespace-nowrap px-4 border-b-2 transition-colors ${activeTab === 'messages' ? 'border-black-deep text-black-deep' : 'border-transparent text-gray-400'}`}>
            Messagerie {unreadCount > 0 && <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unreadCount}</span>}
          </button>
          <button onClick={() => setActiveTab('settings')} className={`flex-1 py-4 text-xs font-bold uppercase tracking-widest text-center whitespace-nowrap px-4 border-b-2 transition-colors ${activeTab === 'settings' ? 'border-black-deep text-black-deep' : 'border-transparent text-gray-400'}`}>
            Paramètres
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 md:p-12 pb-24 md:pb-12">
          
          {/* --- PLANNING --- */}
          {activeTab === 'planning' && (
            <FadeIn>
              <div className="mb-8">
                <h2 className="text-2xl font-serif text-black-deep mb-2">Mon Planning</h2>
                <p className="text-gray-500">Gérez vos interventions et soumettez vos rapports.</p>
              </div>

              <section>
                <h3 className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-4 flex items-center gap-2"><Calendar size={16}/> À venir & En cours</h3>
            
                {upcomingInterventions.length === 0 ? (
                  <div className="bg-white rounded-sm p-6 text-center border border-gray-100 shadow-sm">
                    <p className="text-gray-400 italic">Aucune intervention prévue.</p>
                  </div>
                ) : (
                  <FadeInStagger className="space-y-4">
                    {upcomingInterventions.map(i => {
                      const date = new Date(i.scheduled_at);
                      const isToday = date.toDateString() === new Date().toDateString();
                      const clientCity = i.prestation?.client?.billing_details?.city || 'Ville inconnue';

                      if (editingInterventionId === i.id) {
                        return (
                          <FadeInStaggerItem key={i.id} className="bg-white rounded-sm border p-5 shadow-lg border-gold-300">
                            <h4 className="font-serif text-lg font-bold mb-4 text-black-deep">Modifier l'intervention</h4>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div className="col-span-2">
                                <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">Date</label>
                                <input type="date" value={editIDate} onChange={e => setEditIDate(e.target.value)} className="w-full border border-gray-300 p-2 text-sm focus:outline-none focus:border-gold-500 rounded-sm" />
                              </div>
                              <div>
                                <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">Heure</label>
                                <input type="time" value={editITime} onChange={e => setEditITime(e.target.value)} className="w-full border border-gray-300 p-2 text-sm focus:outline-none focus:border-gold-500 rounded-sm" />
                              </div>
                              <div>
                                <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">Durée (h)</label>
                                <input type="number" min="0.5" step="0.5" value={editIDuration} onChange={e => setEditIDuration(parseFloat(e.target.value))} className="w-full border border-gray-300 p-2 text-sm focus:outline-none focus:border-gold-500 rounded-sm" />
                              </div>
                            </div>
                            <div className="flex gap-2 justify-end">
                              <button onClick={() => setEditingInterventionId(null)} className="px-4 py-2 border border-gray-300 text-sm font-bold uppercase tracking-widest text-gray-500 hover:bg-gray-50 rounded-sm">Annuler</button>
                              <button onClick={() => handleSaveInterventionEdit(i.id)} className="px-4 py-2 bg-gold-500 text-white text-sm font-bold uppercase tracking-widest hover:bg-black-deep rounded-sm transition-colors">Enregistrer</button>
                            </div>
                          </FadeInStaggerItem>
                        );
                      }

                      return (
                        <FadeInStaggerItem key={i.id} className={`bg-white rounded-sm border p-5 shadow-sm transition-all ${isToday ? 'border-gold-500 ring-1 ring-gold-500/50' : 'border-gray-100'}`}>
                          <div className="flex justify-between items-start mb-4">
                            <div className="flex items-center gap-2">
                              {isToday && <span className="bg-gold-500 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm flex items-center gap-1"><Sparkles size={10}/> Aujourd'hui</span>}
                              {i.status === 'en_cours' && <span className="bg-blue-500 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm">En cours</span>}
                            </div>
                            <div className="flex gap-2 text-gray-400">
                              <button onClick={() => startEditIntervention(i)} className="text-xs hover:text-gold-500 font-medium">Modifier horaires</button>
                              {i.status === 'a_venir' && (
                                <button onClick={() => handleUnassignClick(i.id)} className="text-xs hover:text-red-500 font-medium ml-2">Me désister</button>
                              )}
                            </div>
                          </div>

                          <div className="mb-4">
                            <h4 className="font-serif text-xl font-bold text-black-deep">{i.prestation?.client?.full_name}</h4>
                            <p className="text-gray-500 text-sm">{i.prestation?.custom_name} - {clientCity}</p>
                          </div>

                          <div className="flex items-center gap-4 text-sm font-bold text-gray-600 mb-6 bg-gray-50 p-3 rounded-sm">
                            <div className="flex items-center gap-1"><Clock size={16} className="text-gold-500"/> {date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                            <div>{i.duration_hours}h</div>
                            <div className="text-gray-400 ml-auto capitalize">{date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}</div>
                          </div>

                          <div className="flex gap-2">
                            {i.status === 'a_venir' && (
                              <button onClick={() => updateStatus(i.id, 'en_cours')} className="flex-1 bg-black-deep text-white py-3 text-xs font-bold uppercase tracking-widest rounded-sm hover:bg-gold-600 transition-colors">
                                Démarrer l'intervention
                              </button>
                            )}
                            {i.status === 'en_cours' && (
                              <button onClick={() => navigate(`/collaborator/report/${i.id}`)} className="flex-1 bg-gold-500 text-white py-3 text-xs font-bold uppercase tracking-widest rounded-sm hover:bg-gold-600 transition-colors shadow-lg shadow-gold-500/20 flex items-center justify-center gap-2">
                                <Check size={16}/> Terminer & Rapport
                              </button>
                            )}
                          </div>
                        </FadeInStaggerItem>
                      );
                    })}
                  </FadeInStagger>
                )}
              </section>

              <section className="mt-12 opacity-75">
                <h2 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-4 flex items-center gap-2"><Check size={16}/> Historique (30 derniers jours)</h2>
                {pastInterventions.length === 0 ? (
                  <p className="text-gray-400 text-sm italic">Aucune intervention terminée récemment.</p>
                ) : (
                  <div className="space-y-3">
                    {pastInterventions.map(i => {
                      const date = new Date(i.scheduled_at);
                      return (
                        <div key={i.id} className="bg-white rounded-sm border border-gray-100 p-4 flex justify-between items-center grayscale hover:grayscale-0 transition-all">
                          <div>
                            <h4 className="font-serif font-bold text-gray-700">{i.prestation?.client?.full_name}</h4>
                            <p className="text-xs text-gray-400">{date.toLocaleDateString('fr-FR')} à {date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit'})}</p>
                          </div>
                          <span className="text-[10px] font-bold uppercase tracking-widest bg-gray-100 text-gray-500 px-2 py-1 rounded-sm">Terminé</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </FadeIn>
          )}

          {/* --- AVAILABLE INTERVENTIONS --- */}
          {activeTab === 'available' && (
            <FadeIn>
              <div className="mb-8">
                <h2 className="text-2xl font-serif text-black-deep mb-2">Missions Disponibles</h2>
                <p className="text-gray-500">Consultez les demandes en attente de collaborateur et affectez-vous à une intervention.</p>
              </div>

              {availableInterventions.length === 0 ? (
                <div className="bg-white rounded-sm p-12 text-center border border-gray-100 shadow-sm flex flex-col items-center">
                  <Check size={48} className="text-gray-300 mb-4" />
                  <p className="text-gray-500 text-lg">Aucune mission disponible pour le moment.</p>
                  <p className="text-gray-400 text-sm mt-2">Le planning est complet.</p>
                </div>
              ) : (
                <FadeInStagger className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {availableInterventions.map(i => {
                    const date = new Date(i.scheduled_at);
                    const clientCity = i.prestation?.client?.billing_details?.city || 'Ville inconnue';
                    return (
                      <FadeInStaggerItem key={i.id} className="bg-white rounded-sm border border-gold-200 p-5 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-16 h-16 bg-gold-50 rounded-bl-full -z-10 transition-transform group-hover:scale-110"></div>
                        
                        <div className="mb-4">
                          <h4 className="font-serif text-xl font-bold text-black-deep">{i.prestation?.client?.full_name}</h4>
                          <p className="text-gray-500 text-sm">{i.prestation?.custom_name} - {clientCity}</p>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-3 text-sm font-bold text-gray-600 mb-6 bg-cream-50 p-3 rounded-sm border border-gold-100/50">
                          <div className="flex items-center gap-1"><Calendar size={14} className="text-gold-500"/> {date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
                          <div className="flex items-center gap-1"><Clock size={14} className="text-gold-500"/> {date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                          <div className="flex items-center gap-1"><Clock size={14} className="text-gold-500"/> {i.duration_hours}h</div>
                        </div>

                        <button onClick={() => handleAssign(i.id)} className="w-full bg-black-deep text-white py-3 px-4 text-xs font-bold uppercase tracking-widest rounded-sm hover:bg-gold-500 transition-colors flex items-center justify-center gap-2">
                          <PlusCircle size={16} /> M'affecter
                        </button>
                      </FadeInStaggerItem>
                    );
                  })}
                </FadeInStagger>
              )}
            </FadeIn>
          )}

          {activeTab === 'protocols' && (
          <FadeIn>
            <AdminProtocols supabase={supabase} clients={clients} />
          </FadeIn>
        )}

        {/* --- MESSAGES --- */}
          {activeTab === 'messages' && (
            <FadeIn className="h-full flex flex-col">
              <div className="mb-6">
                <h2 className="text-2xl font-serif text-black-deep mb-2">Messagerie Clients</h2>
                <p className="text-gray-500">Communiquez avec les clients concernant vos interventions.</p>
              </div>

              <div className="flex-1 bg-white rounded-sm border border-gray-100 shadow-xl overflow-hidden flex flex-col md:flex-row min-h-[500px]">
                {/* Liste des requêtes */}
                <div className="w-full md:w-1/3 border-r border-gray-100 flex flex-col">
                  <div className="p-4 border-b border-gray-100 bg-gray-50">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500">Discussions</h3>
                  </div>
                  <div className="flex-1 overflow-y-auto hide-scrollbar">
                    {requests.length === 0 ? (
                      <div className="p-6 text-center text-gray-400 text-sm italic">Aucune discussion en cours.</div>
                    ) : (
                      requests.map(req => {
                        const unread = (req.messages || []).filter((m: any) => !m.read_by?.includes(user?.id) && m.sender_id !== user?.id).length;
                        return (
                          <button
                            key={req.id}
                            onClick={() => { setActiveRequest(req); markAsRead(req); }}
                            className={`w-full text-left p-4 border-b border-gray-100 hover:bg-cream-50 transition-colors flex items-start justify-between ${activeRequest?.id === req.id ? 'bg-gold-50 border-l-4 border-l-gold-500' : ''}`}
                          >
                            <div className="overflow-hidden">
                              <p className="font-bold text-sm text-black-deep truncate">{req.client?.full_name}</p>
                              <p className="text-xs text-gray-500 truncate">{req.subject}</p>
                            </div>
                            {unread > 0 && (
                              <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ml-2">{unread}</span>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Zone de chat */}
                <div className="w-full md:w-2/3 flex flex-col bg-cream-50/30">
                  {activeRequest ? (
                    <>
                      <div className="p-4 border-b border-gray-100 bg-white flex justify-between items-center">
                        <div>
                          <h3 className="font-bold text-black-deep">{activeRequest.subject}</h3>
                          <p className="text-xs text-gray-500">Client : {activeRequest.client?.full_name}</p>
                        </div>
                        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm ${activeRequest.status === 'ouvert' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                          {activeRequest.status}
                        </span>
                      </div>

                      <div className="flex-1 overflow-y-auto p-4 space-y-4 hide-scrollbar bg-cream-50/50">
                        {activeRequest.messages?.map((msg: any) => {
                          const isMe = msg.sender_id === user?.id;
                          const isAdmin = msg.sender?.role === 'admin';
                          return (
                            <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                              <span className="text-[10px] text-gray-400 mb-1 ml-1">{isMe ? 'Vous' : (isAdmin ? 'Administration' : msg.sender?.full_name)}</span>
                              <div className={`p-3 rounded-md max-w-[85%] text-sm ${isMe ? 'bg-black-deep text-white rounded-br-none' : (isAdmin ? 'bg-gold-100 text-black-deep border border-gold-200 rounded-bl-none' : 'bg-white border border-gray-200 text-black-deep rounded-bl-none shadow-sm')}`}>
                                {msg.content}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="p-4 bg-white border-t border-gray-100">
                        <form onSubmit={handleSendMessage} className="flex gap-2">
                          <input
                            type="text"
                            value={messageContent}
                            onChange={(e) => setMessageContent(e.target.value)}
                            placeholder="Écrivez votre message..."
                            className="flex-1 border border-gray-300 rounded-sm px-4 py-2 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500"
                            disabled={sendingMessage || activeRequest.status === 'ferme'}
                          />
                          <button
                            type="submit"
                            disabled={sendingMessage || !messageContent.trim() || activeRequest.status === 'ferme'}
                            className="bg-gold-500 text-white px-4 py-2 rounded-sm hover:bg-black-deep transition-colors disabled:opacity-50 flex items-center justify-center"
                          >
                            <Send size={18} />
                          </button>
                        </form>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8 text-center">
                      <Send size={48} className="mb-4 text-gray-300" />
                      <p>Sélectionnez une discussion pour afficher les messages.</p>
                    </div>
                  )}
                </div>
              </div>
            </FadeIn>
          )}

          {/* --- SETTINGS --- */}
          {activeTab === 'settings' && (
            <FadeIn>
              <div className="mb-8">
                <h2 className="text-2xl font-serif text-black-deep mb-2">Paramètres du Profil</h2>
                <p className="text-gray-500">Mettez à jour vos informations personnelles et de facturation.</p>
              </div>

              <div className="bg-white rounded-sm border border-gray-100 shadow-xl p-6 md:p-8">
                <form onSubmit={handleUpdateSettings} className="space-y-6">
                  {settingsMessage && (
                    <div className={`p-4 rounded-sm text-sm font-bold flex items-center gap-2 ${settingsMessage.includes('succès') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {settingsMessage.includes('succès') ? <Check size={16}/> : <AlertTriangle size={16}/>}
                      {settingsMessage}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-4 border-b pb-2">Informations Générales</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Nom complet</label>
                          <input type="text" value={settingsFullName} onChange={e => setSettingsFullName(e.target.value)} className="w-full border border-gray-300 p-3 text-sm focus:outline-none focus:border-gold-500 rounded-sm" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Nouveau mot de passe (Optionnel)</label>
                          <input type="password" value={settingsPassword} onChange={e => setSettingsPassword(e.target.value)} className="w-full border border-gray-300 p-3 text-sm focus:outline-none focus:border-gold-500 rounded-sm" placeholder="Laisser vide pour ne pas changer" />
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-4 border-b pb-2 flex items-center gap-2"><ShieldCheck size={16}/> Facturation & Légal</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Adresse postale</label>
                          <input type="text" value={settingsAddress} onChange={e => setSettingsAddress(e.target.value)} className="w-full border border-gray-300 p-3 text-sm focus:outline-none focus:border-gold-500 rounded-sm" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Code Postal</label>
                            <input type="text" value={settingsPostalCode} onChange={e => setSettingsPostalCode(e.target.value)} className="w-full border border-gray-300 p-3 text-sm focus:outline-none focus:border-gold-500 rounded-sm" />
                          </div>
                          <div>
                            <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Ville</label>
                            <input type="text" value={settingsCity} onChange={e => setSettingsCity(e.target.value)} className="w-full border border-gray-300 p-3 text-sm focus:outline-none focus:border-gold-500 rounded-sm" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">N° de SIRET</label>
                          <input type="text" value={settingsSiret} onChange={e => setSettingsSiret(e.target.value)} className="w-full border border-gray-300 p-3 text-sm focus:outline-none focus:border-gold-500 rounded-sm" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">N° de TVA Intracommunautaire</label>
                          <input type="text" value={settingsVatNumber} onChange={e => setSettingsVatNumber(e.target.value)} className="w-full border border-gray-300 p-3 text-sm focus:outline-none focus:border-gold-500 rounded-sm" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">IBAN (Règlement prestations)</label>
                          <input type="text" value={settingsIban} onChange={e => setSettingsIban(e.target.value)} className="w-full border border-gray-300 p-3 text-sm focus:outline-none focus:border-gold-500 rounded-sm font-mono" placeholder="FR76..." />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-gray-100 flex justify-end">
                    <button type="submit" disabled={settingsLoading} className="bg-black-deep text-white px-8 py-3 text-sm font-bold uppercase tracking-widest hover:bg-gold-600 transition-colors rounded-sm disabled:opacity-50">
                      {settingsLoading ? 'Enregistrement...' : 'Enregistrer les modifications'}
                    </button>
                  </div>
                </form>
              </div>
            </FadeIn>
          )}

        </div>
      </main>

      {/* Report Modal */}
      {activeIntervention && (
        <div className="fixed inset-0 bg-black-deep/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
            <button onClick={() => setActiveIntervention(null)} className="absolute top-4 right-4 text-gray-400 hover:text-black-deep z-10"><X size={24}/></button>
            <div className="p-8">
              <h3 className="font-serif text-2xl font-bold text-black-deep mb-2">Rapport d'Intervention</h3>
              <p className="text-gray-500 text-sm mb-6 pb-6 border-b border-gray-100">Client : {activeIntervention.prestation?.client?.full_name} <br/> Prestation : {activeIntervention.prestation?.custom_name}</p>
              
              <form onSubmit={submitReport} className="space-y-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Notes Générales</label>
                  <textarea value={generalNotes} onChange={e => setGeneralNotes(e.target.value)} required rows={3} className="w-full border border-gray-300 rounded-sm p-3 focus:border-gold-500 focus:outline-none text-sm" placeholder="Résumé de l'intervention..."></textarea>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Problèmes rencontrés <span className="text-gray-400 normal-case font-normal">(Optionnel)</span></label>
                  <textarea value={problems} onChange={e => setProblems(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-sm p-3 focus:border-gold-500 focus:outline-none text-sm" placeholder="Tâches impossibles à réaliser, manque de matériel..."></textarea>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Tâches spéciales réalisées <span className="text-gray-400 normal-case font-normal">(Optionnel)</span></label>
                  <textarea value={specialTasks} onChange={e => setSpecialTasks(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-sm p-3 focus:border-gold-500 focus:outline-none text-sm" placeholder="Vitres, four, intérieur frigo..."></textarea>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">À prévoir pour la prochaine fois <span className="text-gray-400 normal-case font-normal">(Optionnel)</span></label>
                  <textarea value={nextFocus} onChange={e => setNextFocus(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-sm p-3 focus:border-gold-500 focus:outline-none text-sm" placeholder="Insister sur les plinthes, amener plus de détartrant..."></textarea>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Photos <span className="text-gray-400 normal-case font-normal">(Optionnel)</span></label>
                  <div className="border-2 border-dashed border-gray-300 rounded-sm p-6 text-center hover:border-gold-500 transition-colors cursor-pointer bg-gray-50" onClick={() => fileInputRef.current?.click()}>
                    <Camera size={24} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-sm font-medium text-gray-600">Cliquez pour ajouter des photos</p>
                    <input type="file" ref={fileInputRef} className="hidden" multiple accept="image/*" onChange={handlePhotoSelect} />
                  </div>
                  {photos.length > 0 && (
                    <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                      {photos.map((p, i) => (
                        <div key={i} className="relative w-20 h-20 shrink-0 bg-gray-100 rounded-sm overflow-hidden group">
                          <img src={URL.createObjectURL(p)} alt="Preview" className="w-full h-full object-cover" />
                          <button type="button" onClick={(e) => { e.stopPropagation(); removePhoto(i); }} className="absolute inset-0 bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end pt-6 border-t border-gray-100">
                  <button type="submit" disabled={reportLoading} className="bg-gold-500 text-white px-8 py-3 font-bold uppercase tracking-widest hover:bg-black-deep transition-colors text-sm rounded-sm disabled:opacity-50 flex items-center gap-2 shadow-xl shadow-gold-500/20">
                    {reportLoading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Envoi...</> : <><Upload size={18}/> Soumettre le rapport</>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Unassign Justification Modal */}
      {unassignModalIntervention && (
        <div className="fixed inset-0 bg-black-deep/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-sm max-w-md w-full relative">
            <button onClick={() => setUnassignModalIntervention(null)} className="absolute top-4 right-4 text-gray-400 hover:text-black-deep z-10"><X size={24}/></button>
            <div className="p-8">
              <div className="flex items-center gap-3 mb-4 text-red-500">
                <AlertTriangle size={24} />
                <h3 className="font-serif text-xl font-bold text-black-deep">Se désister</h3>
              </div>
              <p className="text-gray-500 text-sm mb-6">
                Veuillez indiquer la raison de votre désistement. Cette justification sera transmise à l'administration pour réorganiser le planning.
              </p>
              
              <form onSubmit={submitUnassign} className="space-y-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Justification obligatoire</label>
                  <textarea 
                    value={unassignReason} 
                    onChange={e => setUnassignReason(e.target.value)} 
                    required 
                    rows={4} 
                    className="w-full border border-red-200 bg-red-50/30 rounded-sm p-3 focus:border-red-500 focus:outline-none text-sm" 
                    placeholder="Ex: Maladie, problème de véhicule, impératif personnel..."
                  ></textarea>
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <button type="button" onClick={() => setUnassignModalIntervention(null)} className="px-4 py-2 border border-gray-300 text-sm font-bold uppercase tracking-widest text-gray-500 hover:bg-gray-50 rounded-sm">Annuler</button>
                  <button type="submit" disabled={unassignLoading || !unassignReason.trim()} className="px-4 py-2 bg-red-500 text-white text-sm font-bold uppercase tracking-widest hover:bg-red-600 rounded-sm transition-colors disabled:opacity-50 flex items-center gap-2">
                    {unassignLoading ? 'Traitement...' : 'Confirmer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
